<script setup lang="ts">
	import { Head, router } from '@inertiajs/vue3';
	import { computed, onMounted, shallowRef } from 'vue';
	import BrowserMediaPermissionManager from '../../utils/BrowserMediaPermissionManager';

	interface MediaPermissionsProps {
		continueUrl: string;
	}

	type PermissionState = 'idle' | 'checking' | 'granted' | 'denied' | 'error';

	const props = defineProps<MediaPermissionsProps>();

	const permissionState = shallowRef<PermissionState>('idle');
	const cameraGranted = shallowRef(false);
	const microphoneGranted = shallowRef(false);
	const errorMessage = shallowRef('');

	const isChecking = computed(() => permissionState.value === 'checking');
	const isGranted = computed(
		() =>
			permissionState.value === 'granted' &&
			cameraGranted.value &&
			microphoneGranted.value
	);
	const statusText = computed(() => {
		if (isGranted.value) {
			return 'Permissões liberadas';
		}

		if (isChecking.value) {
			return 'Aguardando resposta do navegador';
		}

		if (permissionState.value === 'denied') {
			return 'Permissão negada';
		}

		if (permissionState.value === 'error') {
			return 'Não foi possível acessar os dispositivos';
		}

		return 'Aguardando autorização';
	});

	async function requestPermissions() {
		if (isChecking.value) {
			return;
		}

		permissionState.value = 'checking';
		errorMessage.value = '';
		cameraGranted.value = false;
		microphoneGranted.value = false;

		try {
			if (!navigator.mediaDevices?.getUserMedia) {
				throw new Error(
					'Este navegador não permite solicitar câmera e microfone.'
				);
			}

			await BrowserMediaPermissionManager.requestBrowserMediaPermissions();

			cameraGranted.value = await BrowserMediaPermissionManager.browserHasCameraPermission();
			microphoneGranted.value = await BrowserMediaPermissionManager.browserHasMicrophonePermission();

			if (!cameraGranted.value || !microphoneGranted.value) {
				throw new Error(
					'Câmera e microfone precisam estar disponíveis.'
				);
			}

			permissionState.value = 'granted';
			continueToOpenCamera();
		} catch (error) {
			const errorName = error instanceof DOMException ? error.name : '';
			permissionState.value =
				errorName === 'NotAllowedError' ||
				errorName === 'PermissionDeniedError'
					? 'denied'
					: 'error';
			errorMessage.value = getPermissionErrorMessage(error);
		}
	}

	function getPermissionErrorMessage(error: unknown): string {
		if (error instanceof DOMException) {
			if (
				error.name === 'NotAllowedError' ||
				error.name === 'PermissionDeniedError'
			) {
				return 'Libere o acesso à câmera e ao microfone nas configurações do navegador e atualize esta página.';
			}

			if (
				error.name === 'NotFoundError' ||
				error.name === 'DevicesNotFoundError'
			) {
				return 'Não encontramos câmera ou microfone conectados a este computador.';
			}

			if (
				error.name === 'NotReadableError' ||
				error.name === 'TrackStartError'
			) {
				return 'Câmera ou microfone estão em uso por outro aplicativo. Feche o aplicativo e tente novamente.';
			}
		}

		if (error instanceof Error) {
			return error.message;
		}

		return 'Não foi possível pedir permissão para câmera e microfone.';
	}

	function continueToOpenCamera() {
		if (!isGranted.value) {
			return;
		}

		router.visit(props.continueUrl);
	}

	onMounted(() => {
		requestPermissions();
	});
</script>

<template>
	<Head title="Permissão de câmera e microfone - Prova" />

	<div class="media-permissions-page">
		<div class="container">
			<div class="row align-items-center mt-10 mb-10 row-logo">
				<div class="col mx-auto text-center">
					<img
						alt="Logo Habilite-se"
						src="/habil/images/logo.svg"
						class="h-35px" />
				</div>
			</div>

			<div class="row align-items-center row-card">
				<div class="col-sm-10 col-md-9 col-lg-8 mx-auto">
					<div class="card permission-card">
						<div class="card-body permission-card-body">
							<div class="permission-header">
								<div class="permission-icon">
									<i class="fas fa-video"></i>
								</div>
								<div>
									<p class="permission-kicker">
										Etapa obrigatória
									</p>
									<h2 class="permission-title">
										Permitir câmera e microfone
									</h2>
									<p class="permission-description">
										Antes da liberação da prova, o navegador
										precisa acessar a webcam e o microfone
										deste computador.
									</p>
								</div>
							</div>

							<div class="permission-status-panel">
								<div class="permission-status-main">
									<span
										class="permission-status-dot"
										:class="{
											'is-granted': isGranted,
											'is-checking': isChecking,
											'is-blocked':
												permissionState === 'denied' ||
												permissionState === 'error',
										}"></span>
									<span>{{ statusText }}</span>
								</div>
							</div>

							<div
								v-if="errorMessage"
								class="permission-alert"
								role="alert">
								<i class="fas fa-exclamation-triangle"></i>
								<span>{{ errorMessage }}</span>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped>
	.media-permissions-page {
		min-height: 100vh;
		background: linear-gradient(
				135deg,
				rgba(245, 247, 250, 0.94),
				rgba(234, 239, 246, 0.94)
			),
			radial-gradient(
				circle at top left,
				rgba(0, 158, 247, 0.16),
				transparent 34%
			);
	}

	.permission-card {
		border: 1px solid #e4e9f2;
		border-radius: 8px;
		box-shadow: 0 18px 48px rgba(27, 39, 64, 0.1);
	}

	.permission-card-body {
		padding: 36px;
	}

	.permission-header {
		display: grid;
		grid-template-columns: 64px 1fr;
		gap: 20px;
		align-items: start;
	}

	.permission-icon {
		display: grid;
		width: 64px;
		height: 64px;
		place-items: center;
		border-radius: 8px;
		background: #eef7ff;
		color: #009ef7;
		font-size: 28px;
	}

	.permission-kicker {
		margin: 0 0 6px;
		color: #7e8a9a;
		font-size: 12px;
		font-weight: 700;
		text-transform: uppercase;
	}

	.permission-title {
		margin: 0;
		color: #181c32;
		font-size: 26px;
		font-weight: 700;
		letter-spacing: 0;
	}

	.permission-description {
		margin: 10px 0 0;
		color: #5e6675;
		font-size: 15px;
		line-height: 1.55;
	}

	.permission-status-panel {
		margin-top: 28px;
		padding: 22px;
		border: 1px solid #edf0f5;
		border-radius: 8px;
		background: #fbfcfe;
	}

	.permission-status-main {
		display: flex;
		align-items: center;
		gap: 10px;
		color: #3f4654;
		font-size: 16px;
		font-weight: 700;
	}

	.permission-status-dot {
		width: 12px;
		height: 12px;
		border-radius: 50%;
		background: #a1a5b7;
	}

	.permission-status-dot.is-granted {
		background: #50cd89;
	}

	.permission-status-dot.is-checking {
		background: #ffc700;
	}

	.permission-status-dot.is-blocked {
		background: #f1416c;
	}

	.permission-device-grid {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 12px;
		margin-top: 18px;
	}

	.permission-device {
		display: grid;
		grid-template-columns: 24px 1fr 24px;
		gap: 10px;
		align-items: center;
		padding: 14px;
		border: 1px solid #edf0f5;
		border-radius: 8px;
		background: #ffffff;
		color: #3f4654;
		font-weight: 700;
	}

	.permission-alert {
		display: flex;
		gap: 10px;
		align-items: flex-start;
		margin-top: 18px;
		padding: 14px 16px;
		border: 1px solid #ffd7df;
		border-radius: 8px;
		background: #fff5f8;
		color: #b42346;
		font-weight: 600;
		line-height: 1.45;
	}

	@media (max-width: 640px) {
		.permission-card-body {
			padding: 24px;
		}

		.permission-header {
			grid-template-columns: 1fr;
		}

		.permission-device-grid {
			grid-template-columns: 1fr;
		}
	}
</style>
